import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { ArrowRight, Play, Shield, Star } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const stats = [
  { value: 4200, suffix: "+", label: "Active traders" },
  { value: 2847, suffix: "", label: "Alerts today" },
  { value: 94, suffix: "%", label: "Catch rate" },
];

const AnimatedNumber = ({
  target,
  suffix,
}: {
  target: number;
  suffix: string;
}) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [target]);

  return (
    <span>
      {count.toLocaleString()}
      {suffix}
    </span>
  );
};

const Hero = () => {
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);

  return (
    <>
      <section className="relative pt-28 pb-16 md:pt-36 md:pb-28 px-6 overflow-hidden">
        {/* Subtle background texture */}
        <div className="absolute inset-0 grain pointer-events-none" />

        {/* Organic background shapes */}
        <div className="absolute top-20 right-[10%] w-[500px] h-[500px] bg-accent/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-[5%] w-[400px] h-[400px] bg-primary/5 rounded-full blur-3xl" />

        <div className="container mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
            {/* Left content */}
            <div className="max-w-xl">
              {/* Trust badge */}
              <div className="inline-flex items-center gap-2 bg-accent/8 border border-accent/20 text-accent px-3 py-1.5 rounded-full text-sm font-medium mb-6">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-accent"></span>
                </span>
                <span>Live — tracking volume surges right now</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-[3.5rem] font-bold leading-[1.08] mb-5 text-balance">
                Catch volume spikes{" "}
                <span className="text-accent">before everyone else.</span>
              </h1>

              <p className="text-base md:text-lg text-muted-foreground mb-8 leading-relaxed">
                Real-time SMS & email alerts when low-float stocks see unusual
                volume. Built by traders, for traders who refuse to be late.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <Button
                  variant="cta"
                  size="lg"
                  className="text-base font-semibold group"
                  asChild
                >
                  <Link to="/signup">
                    Start free for 14 days
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                  </Link>
                </Button>

                <Button
                  variant="outline"
                  size="lg"
                  className="text-base group"
                  onClick={() => setVideoDialogOpen(true)}
                >
                  <Play className="mr-2 h-4 w-4 fill-current" />
                  Watch demo
                </Button>
              </div>

              {/* Trust signals */}
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <Shield className="h-3.5 w-3.5 text-accent" />
                  <span>No credit card</span>
                </div>
                <span className="hidden sm:inline text-border">•</span>
                <div className="flex items-center gap-1.5">
                  <Star className="h-3.5 w-3.5 text-cta fill-cta" />
                  <span>4.9/5 from 280+ reviews</span>
                </div>
              </div>
            </div>

            {/* Right - Live monitor card */}
            <div className="w-full max-w-md mx-auto lg:max-w-none">
              <div className="bg-card rounded-xl border border-border shadow-2xl overflow-hidden">
                {/* Window chrome */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-destructive/80"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-cta/80"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-accent/80"></div>
                  </div>
                  <span className="text-xs text-muted-foreground font-medium">
                    Live Feed
                  </span>
                </div>

                <div className="p-4 space-y-3">
                  {/* Alert item 1 - highlighted */}
                  <div className="bg-accent/8 border border-accent/20 rounded-lg p-3.5 animate-pulse-subtle">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-foreground">
                            BITX
                          </span>
                          <span className="text-xs px-1.5 py-0.5 bg-accent/20 text-accent rounded font-medium">
                            ALERT
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Volume 847% above avg · $42.10
                        </p>
                      </div>
                      <div className="text-right">
                        <span className="text-accent font-bold text-lg">
                          +23.4%
                        </span>
                        <p className="text-xs text-muted-foreground">
                          2 min ago
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Alert item 2 */}
                  <div className="bg-muted/40 rounded-lg p-3.5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-foreground">
                            MSTZ
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Volume 524% above avg · $18.50
                        </p>
                      </div>
                      <div className="text-right">
                        <span className="text-accent font-semibold">
                          +15.2%
                        </span>
                        <p className="text-xs text-muted-foreground">
                          8 min ago
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Alert item 3 */}
                  <div className="bg-muted/40 rounded-lg p-3.5 opacity-70">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-foreground">
                            NVDA
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Volume 312% above avg · $134.50
                        </p>
                      </div>
                      <div className="text-right">
                        <span className="text-accent font-semibold">+8.7%</span>
                        <p className="text-xs text-muted-foreground">
                          14 min ago
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats bar */}
          <div className="mt-16 pt-10 border-t border-border/60">
            <div className="grid grid-cols-3 gap-4 md:gap-8 max-w-2xl mx-auto">
              {stats.map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-foreground mb-1">
                    <AnimatedNumber target={stat.value} suffix={stat.suffix} />
                  </div>
                  <p className="text-xs md:text-sm text-muted-foreground">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Dialog open={videoDialogOpen} onOpenChange={setVideoDialogOpen}>
        <DialogContent className="sm:max-w-3xl p-0 overflow-hidden">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle>See RadarTicker in Action</DialogTitle>
          </DialogHeader>
          <div className="p-6 pt-4">
            <div className="aspect-video bg-muted rounded-lg flex items-center justify-center border border-border">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Play className="h-8 w-8 text-primary fill-current" />
                </div>
                <p className="text-muted-foreground text-sm">
                  Demo video coming soon
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  See how traders catch volume spikes in real-time
                </p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Hero;
